import axios, { AxiosInstance } from 'axios';

declare module 'axios' {
  // A request that answers 402 itself — with a feature-specific paywall —
  // rather than letting the interceptor's generic prompt speak first.
  export interface AxiosRequestConfig {
    handlesUpgrade?: boolean;
  }
}

const determineBaseURL = () => {
  // Local development (highest priority)
  const isDevelopment = window.location.hostname === 'localhost' ||
                       window.location.hostname === '127.0.0.1';
  if (isDevelopment) {
    return 'http://localhost:8000/api';
  }

  // Environment override (set in the Railway frontend service)
  if (process.env.REACT_APP_API_URL) {
    return `${process.env.REACT_APP_API_URL}/api`;
  }

  // Deployed fallback
  const isRailwayFrontend = window.location.hostname === 'yarnboard.app' ||
                            window.location.hostname.includes('railway.app');
  if (isRailwayFrontend) {
    return 'https://yarnboard-production.up.railway.app/api';
  }

  return '/api';
};

const baseURL = determineBaseURL();

const api: AxiosInstance = axios.create({
  baseURL: baseURL,
  withCredentials: true,
});

// Helper function to get CSRF token from cookie
const getCSRFToken = (): string | null => {
  const name = 'csrftoken';
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
};

api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  const publicEndpoints = ['/auth/register/', '/auth/token/', '/auth/login/', '/auth/google/'];
  const isPublicEndpoint = publicEndpoints.some(endpoint => config.url?.includes(endpoint));

  if (!isPublicEndpoint && token) {
    config.headers.Authorization = `Token ${token}`;
  }

  if (config.method && ['post', 'put', 'patch', 'delete'].includes(config.method?.toLowerCase() || '')) {
    const csrfToken = getCSRFToken();
    if (csrfToken) {
      config.headers['X-CSRFToken'] = csrfToken;
    }
  }

  return config;
});

api.interceptors.response.use(
  response => response,
  error => {
    const status = error.response?.status;
    const onAuthPage = ['/login', '/register', '/welcome'].some(p =>
      window.location.pathname.startsWith(p)
    );

    if (status === 401 && !onAuthPage) {
      // Token is invalid/expired: log out once, globally
      localStorage.removeItem('token');
      window.location.href = '/login';
      return new Promise(() => {}); // halt the original caller
    }

    if (status === 402 && !error.config?.handlesUpgrade) {
      // Free-tier limit / premium feature: offer the pricing page once,
      // then still reject so the calling UI can show its own message.
      const detail = error.response?.data?.detail || 'This is a premium feature.';
      if (window.confirm(`${detail}\n\nView plans?`)) {
        window.location.href = '/pricing';
        return new Promise(() => {});
      }
    }

    if (!error.response) {
      // Network-level failure (server unreachable, CORS, DNS) — make it
      // loud instead of letting callers render silent empty states
      console.error('[API] Network error — server unreachable:', error.message);
    }

    return Promise.reject(error);
  }
);

export default api;
